import { DreamComponentsProvider } from '@shared/dream-components';
import type { PropsWithChildren } from 'react';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { Page } from '~/interfaces/page';
import type { Site } from '~/interfaces/site';
import { NavbarSerializer, PageSerializer } from '../DreamBuilder';
import FooterSerializer from '../DreamBuilder/FooterSerializer';
import GlobalElementsSerializer from '../DreamBuilder/GlobalElementsSerializer';
import NavbarSerializerV2 from '../DreamBuilder/NavbarSerializerV2';
import SearchModal from '../DreamBuilder/nodes/SearchModal/SearchModal';

type Props = PropsWithChildren<{
  site: Site;
  page?: Page;
  forceShowNavbar?: boolean;
  forceShowFooter?: boolean;
  isSearchModalEnabled?: boolean;
}>;

const StaticPage = ({ page, site, children, forceShowNavbar, forceShowFooter, isSearchModalEnabled = true }: Props) => {
  if (!site) {
    // show the refresh page message if we didn't get loader data
    throw new Error('RefreshPageError');
  }

  const { isInProgress } = useSignupRedirects();

  // Using !== false to check if explicitly set to false
  // New pages by default have empty customization
  const showNavbar =
    forceShowNavbar === true || (page?.viewable_page_version?.customization?.show_navbar !== false && !isInProgress);
  const showFooter =
    forceShowFooter === true || (page?.viewable_page_version?.customization?.show_footer !== false && !isInProgress);
  const showNavbarV2 = site?.navbar_v2_enabled;

  return (
    <DreamComponentsProvider context={{ responsiveImagesEnabled: true }}>
      {showNavbar && (showNavbarV2 ? <NavbarSerializerV2 site={site} page={page} /> : <NavbarSerializer site={site} />)}
      {page && <PageSerializer site={site} page={page} isFooterVisible={showFooter} />}
      {children}
      {showFooter && <FooterSerializer site={site} />}
      <GlobalElementsSerializer page={page} site={site} />
      {isSearchModalEnabled && <SearchModal />}
    </DreamComponentsProvider>
  );
};

export default StaticPage;
